import { useState, useEffect, useCallback } from "react";
import axios, { getApiUrl } from "@/lib/api";
import { toast } from "sonner";
import {
  Bell,
  Plus,
  Edit2,
  Trash2,
  Copy,
  Clock,
  Calendar,
  Gamepad2,
  Trophy,
  Target,
  Search,
  Filter,
  ChevronDown,
  AlarmClock,
  AlarmClockOff,
  Repeat,
  CalendarDays,
  CalendarClock,
  ToggleLeft,
  ToggleRight,
  Loader2,
  X,
  Check,
  BellOff,
  BellRing,
  Timer,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Checkbox } from "@/components/ui/checkbox";
import { AdBanner } from "@/components/AdSense";

const API = `${getApiUrl()}/api`;

const scheduleTypeOptions = [
  { value: "once", label: "One Time", icon: Calendar },
  { value: "daily", label: "Daily", icon: CalendarDays },
  { value: "weekly", label: "Weekly", icon: Repeat },
  { value: "monthly", label: "Monthly", icon: CalendarClock },
  { value: "yearly", label: "Yearly", icon: CalendarDays },
  { value: "custom", label: "Every X Days", icon: Timer },
];

const weekDays = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const snoozeOptions = [
  { value: 10, label: "10 minutes" },
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 1440, label: "1 day" },
];

const sortOptions = [
  { value: "upcoming", label: "Upcoming" },
  { value: "name", label: "Name" },
  { value: "game", label: "Game" },
  { value: "achievement", label: "Achievement" },
  { value: "challenge", label: "Challenge" },
  { value: "createdAt", label: "Date Created" },
];

// Helper to format date for display
const formatDate = (isoString) => {
  if (!isoString) return "Not scheduled";
  try {
    const date = new Date(isoString);
    return date.toLocaleString(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return isoString;
  }
};

// Helper to get relative time
const getRelativeTime = (isoString) => {
  if (!isoString) return null;
  try {
    const date = new Date(isoString);
    const now = new Date();
    const diff = date - now;
    
    if (diff < 0) return "Overdue";
    
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(minutes / 60);
    const days = Math.floor(hours / 24);
    
    if (days > 0) return `in ${days}d ${hours % 24}h`;
    if (hours > 0) return `in ${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `in ${minutes}m`;
    return "Now";
  } catch {
    return null;
  }
};

export default function Notifications() {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState("upcoming");
  const [sortOrder, setSortOrder] = useState("asc");
  const [searchQuery, setSearchQuery] = useState("");
  const [showDisabled, setShowDisabled] = useState(true);
  
  // Create/Edit dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const [formData, setFormData] = useState({
    name: "",
    scheduleType: "once",
    scheduledDate: "",
    scheduledTime: "09:00",
    customIntervalDays: 1,
    weeklyDays: [],
    monthlyDay: 1,
    yearlyMonth: 1,
    yearlyDay: 1,
    gameId: null,
    achievementId: null,
    challengeId: null,
    linkedItemName: null,
    linkedItemImage: null,
    linkedItemType: null,
  });
  
  // Link item search state
  const [linkSearchOpen, setLinkSearchOpen] = useState(false);
  const [linkSearchType, setLinkSearchType] = useState("game");
  const [linkSearchQuery, setLinkSearchQuery] = useState("");
  const [linkSearchResults, setLinkSearchResults] = useState([]);
  const [linkSearchLoading, setLinkSearchLoading] = useState(false);
  
  // Custom snooze dialog
  const [customSnoozeOpen, setCustomSnoozeOpen] = useState(false);
  const [customSnoozeMinutes, setCustomSnoozeMinutes] = useState(60);
  const [snoozeTargetId, setSnoozeTargetId] = useState(null);

  // Fetch notifications
  const fetchNotifications = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      params.append("sort_by", sortBy);
      params.append("sort_order", sortOrder);
      if (!showDisabled) params.append("enabled_only", "true");
      
      const response = await axios.get(`${API}/notifications?${params}`);
      setNotifications(response.data);
    } catch (error) {
      console.error("Failed to fetch notifications:", error);
      toast.error("Failed to load notifications");
    } finally {
      setLoading(false);
    }
  }, [sortBy, sortOrder, showDisabled]);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Search for linkable items
  const searchLinkableItems = async (type, query) => {
    if (!query.trim()) {
      setLinkSearchResults([]);
      return;
    }
    
    setLinkSearchLoading(true);
    try {
      let results = [];
      
      if (type === "game") {
        const response = await axios.get(`${API}/games?search=${encodeURIComponent(query)}&limit=20`);
        results = response.data.map(g => ({
          id: g.id,
          name: g.name,
          image: g.boxArtUrl,
          type: "game",
          subtitle: `${g.platform} • ${g.unlockedAchievements}/${g.totalAchievements} achievements`,
        }));
      } else if (type === "achievement") {
        const response = await axios.get(`${API}/achievements/search?q=${encodeURIComponent(query)}&limit=20`);
        results = response.data.map(a => ({
          id: a.id,
          name: a.name,
          image: a.imageUrl,
          type: "achievement",
          subtitle: a.gameName || "Unknown game",
          gameId: a.gameId,
        }));
      } else if (type === "challenge") {
        // Search challenges across all games
        const gamesResponse = await axios.get(`${API}/games?limit=100`);
        const allChallenges = [];
        
        for (const game of gamesResponse.data.slice(0, 20)) {
          try {
            const challengesResponse = await axios.get(`${API}/games/${game.id}/challenges`);
            challengesResponse.data.forEach(c => {
              if (c.name.toLowerCase().includes(query.toLowerCase())) {
                allChallenges.push({
                  id: c.id,
                  name: c.name,
                  image: c.imageUrl || game.boxArtUrl,
                  type: "challenge",
                  subtitle: game.name,
                  gameId: game.id,
                });
              }
            });
          } catch {}
        }
        results = allChallenges.slice(0, 20);
      }
      
      setLinkSearchResults(results);
    } catch (error) {
      console.error("Failed to search:", error);
    } finally {
      setLinkSearchLoading(false);
    }
  };

  // Create notification
  const handleCreate = async () => {
    if (!formData.name.trim()) {
      toast.error("Please enter a name for the notification");
      return;
    }
    
    try {
      await axios.post(`${API}/notifications`, formData);
      toast.success("Notification created");
      setDialogOpen(false);
      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error("Failed to create notification:", error);
      toast.error("Failed to create notification");
    }
  };

  // Update notification
  const handleUpdate = async () => {
    if (!editingNotification) return;
    
    try {
      await axios.put(`${API}/notifications/${editingNotification.id}`, formData);
      toast.success("Notification updated");
      setDialogOpen(false);
      setEditingNotification(null);
      resetForm();
      fetchNotifications();
    } catch (error) {
      console.error("Failed to update notification:", error);
      toast.error("Failed to update notification");
    }
  };

  // Delete notification
  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/notifications/${id}`);
      toast.success("Notification deleted");
      fetchNotifications();
    } catch (error) {
      console.error("Failed to delete notification:", error);
      toast.error("Failed to delete notification");
    }
  };

  // Duplicate notification
  const handleDuplicate = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/duplicate`);
      toast.success("Notification duplicated");
      fetchNotifications();
    } catch (error) {
      console.error("Failed to duplicate notification:", error);
      toast.error("Failed to duplicate notification");
    }
  };

  // Toggle notification
  const handleToggle = async (id) => {
    try {
      const response = await axios.post(`${API}/notifications/${id}/toggle`);
      toast.success(response.data.message);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to toggle notification:", error);
      toast.error("Failed to toggle notification");
    }
  };

  // Snooze notification
  const handleSnooze = async (id, minutes) => {
    try {
      await axios.post(`${API}/notifications/${id}/snooze?minutes=${minutes}`);
      toast.success(`Notification snoozed for ${minutes} minutes`);
      fetchNotifications();
    } catch (error) {
      console.error("Failed to snooze notification:", error);
      toast.error("Failed to snooze notification");
    }
  };

  // Unsnooze notification
  const handleUnsnooze = async (id) => {
    try {
      await axios.post(`${API}/notifications/${id}/unsnooze`);
      toast.success("Snooze removed");
      fetchNotifications();
    } catch (error) {
      console.error("Failed to unsnooze:", error);
      toast.error("Failed to remove snooze");
    }
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      name: "",
      scheduleType: "once",
      scheduledDate: "",
      scheduledTime: "09:00",
      customIntervalDays: 1,
      weeklyDays: [],
      monthlyDay: 1,
      yearlyMonth: 1,
      yearlyDay: 1,
      gameId: null,
      achievementId: null,
      challengeId: null,
      linkedItemName: null,
      linkedItemImage: null,
      linkedItemType: null,
    });
  };

  // Open edit dialog
  const openEditDialog = (notification) => {
    setEditingNotification(notification);
    setFormData({
      name: notification.name || "",
      scheduleType: notification.scheduleType || "once",
      scheduledDate: notification.scheduledDate || "",
      scheduledTime: notification.scheduledTime || "09:00",
      customIntervalDays: notification.customIntervalDays || 1,
      weeklyDays: notification.weeklyDays || [],
      monthlyDay: notification.monthlyDay || 1,
      yearlyMonth: notification.yearlyMonth || 1,
      yearlyDay: notification.yearlyDay || 1,
      gameId: notification.gameId || null,
      achievementId: notification.achievementId || null,
      challengeId: notification.challengeId || null,
      linkedItemName: notification.linkedItemName || null,
      linkedItemImage: notification.linkedItemImage || null,
      linkedItemType: notification.linkedItemType || null,
    });
    setDialogOpen(true);
  };

  // Select linked item
  const selectLinkedItem = (item) => {
    setFormData({
      ...formData,
      gameId: item.type === "game" ? item.id : item.gameId || null,
      achievementId: item.type === "achievement" ? item.id : null,
      challengeId: item.type === "challenge" ? item.id : null,
      linkedItemName: item.name,
      linkedItemImage: item.image,
      linkedItemType: item.type,
    });
    setLinkSearchOpen(false);
    setLinkSearchQuery("");
    setLinkSearchResults([]);
  };

  // Clear linked item
  const clearLinkedItem = () => {
    setFormData({
      ...formData,
      gameId: null,
      achievementId: null,
      challengeId: null,
      linkedItemName: null,
      linkedItemImage: null,
      linkedItemType: null,
    });
  };

  // Filter notifications by search
  const filteredNotifications = notifications.filter(n => 
    n.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    n.linkedItemName?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Get schedule type icon
  const getScheduleIcon = (type) => {
    const option = scheduleTypeOptions.find(o => o.value === type);
    return option?.icon || Calendar;
  };

  // Get schedule description
  const getScheduleDescription = (notification) => {
    const { scheduleType, scheduledTime, weeklyDays, monthlyDay, yearlyMonth, yearlyDay, customIntervalDays } = notification;
    
    switch (scheduleType) {
      case "once":
        return `Once at ${scheduledTime}`;
      case "daily":
        return `Daily at ${scheduledTime}`;
      case "weekly":
        const days = weeklyDays?.map(d => weekDays.find(wd => wd.value === d)?.label).join(", ") || "No days";
        return `Weekly on ${days} at ${scheduledTime}`;
      case "monthly":
        return `Monthly on day ${monthlyDay} at ${scheduledTime}`;
      case "yearly":
        return `Yearly on ${yearlyMonth}/${yearlyDay} at ${scheduledTime}`;
      case "custom":
        return `Every ${customIntervalDays} day(s) at ${scheduledTime}`;
      default:
        return "Unknown schedule";
    }
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-white flex items-center gap-3">
            <Bell className="w-6 h-6 sm:w-8 sm:h-8 text-purple-400" />
            Notifications
          </h1>
          <p className="text-white/50 mt-1 text-sm sm:text-base">Manage your game reminders and alerts</p>
        </div>
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setEditingNotification(null);
            resetForm();
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-500 w-full sm:w-auto" data-testid="create-notification-btn">
              <Plus className="w-4 h-4 mr-2" />
              Create Notification
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingNotification ? "Edit Notification" : "Create Notification"}</DialogTitle>
              <DialogDescription>
                Set up a reminder for your gaming goals
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 pt-4">
              {/* Name */}
              <div className="space-y-2">
                <Label>Notification Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Daily Achievement Hunt"
                  className="bg-black/50 border-white/10"
                  data-testid="notification-name-input"
                />
              </div>

              {/* Schedule Type */}
              <div className="space-y-2">
                <Label>Schedule Type</Label>
                <Select
                  value={formData.scheduleType}
                  onValueChange={(v) => setFormData({ ...formData, scheduleType: v })}
                >
                  <SelectTrigger className="bg-black/50 border-white/10" data-testid="schedule-type-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0A0A0A] border-white/10">
                    {scheduleTypeOptions.map((opt) => (
                      <SelectItem key={opt.value} value={opt.value}>
                        <div className="flex items-center gap-2">
                          <opt.icon className="w-4 h-4" />
                          {opt.label}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Schedule Date (for once/custom) */}
              {(formData.scheduleType === "once" || formData.scheduleType === "custom") && (
                <div className="space-y-2">
                  <Label>{formData.scheduleType === "once" ? "Date" : "Start Date"}</Label>
                  <Input
                    type="date"
                    value={formData.scheduledDate}
                    onChange={(e) => setFormData({ ...formData, scheduledDate: e.target.value })}
                    className="bg-black/50 border-white/10"
                  />
                </div>
              )}

              {/* Weekly Days */}
              {formData.scheduleType === "weekly" && (
                <div className="space-y-2">
                  <Label>Days of Week</Label>
                  <div className="flex flex-wrap gap-2">
                    {weekDays.map((day) => (
                      <label
                        key={day.value}
                        className={`flex items-center gap-2 px-3 py-2 rounded cursor-pointer transition-colors ${
                          formData.weeklyDays.includes(day.value)
                            ? "bg-purple-500/30 border border-purple-500/50"
                            : "bg-white/5 border border-white/10 hover:bg-white/10"
                        }`}
                      >
                        <Checkbox
                          checked={formData.weeklyDays.includes(day.value)}
                          onCheckedChange={(checked) => {
                            if (checked) {
                              setFormData({ ...formData, weeklyDays: [...formData.weeklyDays, day.value].sort() });
                            } else {
                              setFormData({ ...formData, weeklyDays: formData.weeklyDays.filter(d => d !== day.value) });
                            }
                          }}
                          className="hidden"
                        />
                        <span className="text-sm">{day.label}</span>
                      </label>
                    ))}
                  </div>
                </div>
              )}

              {/* Monthly Day */}
              {formData.scheduleType === "monthly" && (
                <div className="space-y-2">
                  <Label>Day of Month</Label>
                  <Input
                    type="number"
                    min="1"
                    max="31"
                    value={formData.monthlyDay}
                    onChange={(e) => setFormData({ ...formData, monthlyDay: parseInt(e.target.value) || 1 })}
                    className="bg-black/50 border-white/10"
                  />
                </div>
              )}

              {/* Yearly Month/Day */}
              {formData.scheduleType === "yearly" && (
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Month</Label>
                    <Input
                      type="number"
                      min="1"
                      max="12"
                      value={formData.yearlyMonth}
                      onChange={(e) => setFormData({ ...formData, yearlyMonth: parseInt(e.target.value) || 1 })}
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Day</Label>
                    <Input
                      type="number"
                      min="1"
                      max="31"
                      value={formData.yearlyDay}
                      onChange={(e) => setFormData({ ...formData, yearlyDay: parseInt(e.target.value) || 1 })}
                      className="bg-black/50 border-white/10"
                    />
                  </div>
                </div>
              )}

              {/* Custom Interval */}
              {formData.scheduleType === "custom" && (
                <div className="space-y-2">
                  <Label>Repeat Every (days)</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.customIntervalDays}
                    onChange={(e) => setFormData({ ...formData, customIntervalDays: parseInt(e.target.value) || 1 })}
                    className="bg-black/50 border-white/10"
                  />
                </div>
              )}

              {/* Time */}
              <div className="space-y-2">
                <Label>Time</Label>
                <Input
                  type="time"
                  value={formData.scheduledTime}
                  onChange={(e) => setFormData({ ...formData, scheduledTime: e.target.value })}
                  className="bg-black/50 border-white/10"
                />
              </div>

              {/* Link to Game/Achievement/Challenge */}
              <div className="space-y-2">
                <Label>Link to (optional)</Label>
                {formData.linkedItemName ? (
                  <div className="flex items-center gap-3 p-3 rounded-lg bg-white/5 border border-white/10">
                    {formData.linkedItemImage ? (
                      <img
                        src={formData.linkedItemImage}
                        alt={formData.linkedItemName}
                        className="w-10 h-10 rounded object-contain bg-black/50"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                        {formData.linkedItemType === "game" && <Gamepad2 className="w-5 h-5 text-white/50" />}
                        {formData.linkedItemType === "achievement" && <Trophy className="w-5 h-5 text-white/50" />}
                        {formData.linkedItemType === "challenge" && <Target className="w-5 h-5 text-white/50" />}
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{formData.linkedItemName}</p>
                      <p className="text-xs text-white/50 capitalize">{formData.linkedItemType}</p>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={clearLinkedItem}
                      className="text-white/50 hover:text-white"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setLinkSearchType("game"); setLinkSearchOpen(true); }}
                      className="flex-1 border-white/10 hover:bg-white/5"
                    >
                      <Gamepad2 className="w-4 h-4 mr-2" />
                      Game
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setLinkSearchType("achievement"); setLinkSearchOpen(true); }}
                      className="flex-1 border-white/10 hover:bg-white/5"
                    >
                      <Trophy className="w-4 h-4 mr-2" />
                      Achievement
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => { setLinkSearchType("challenge"); setLinkSearchOpen(true); }}
                      className="flex-1 border-white/10 hover:bg-white/5"
                    >
                      <Target className="w-4 h-4 mr-2" />
                      Challenge
                    </Button>
                  </div>
                )}
              </div>

              {/* Save Button */}
              <Button
                onClick={editingNotification ? handleUpdate : handleCreate}
                className="w-full bg-purple-600 hover:bg-purple-500"
                data-testid="save-notification-btn"
              >
                {editingNotification ? "Save Changes" : "Create Notification"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Link Search Dialog */}
      <Dialog open={linkSearchOpen} onOpenChange={setLinkSearchOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="capitalize">Link {linkSearchType}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <Input
                value={linkSearchQuery}
                onChange={(e) => {
                  setLinkSearchQuery(e.target.value);
                  searchLinkableItems(linkSearchType, e.target.value);
                }}
                placeholder={`Search ${linkSearchType}s...`}
                className="pl-10 bg-black/50 border-white/10"
              />
            </div>
            <div className="max-h-64 overflow-y-auto space-y-2">
              {linkSearchLoading ? (
                <div className="flex justify-center py-4">
                  <Loader2 className="w-6 h-6 animate-spin text-white/50" />
                </div>
              ) : linkSearchResults.length === 0 ? (
                <p className="text-center text-white/50 py-4">
                  {linkSearchQuery ? "No results found" : `Search for a ${linkSearchType}`}
                </p>
              ) : (
                linkSearchResults.map((item) => (
                  <div
                    key={item.id}
                    onClick={() => selectLinkedItem(item)}
                    className="flex items-center gap-3 p-3 rounded-lg bg-white/5 hover:bg-white/10 cursor-pointer transition-colors"
                  >
                    {item.image ? (
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-10 h-10 rounded object-contain bg-black/50"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded bg-white/10 flex items-center justify-center">
                        <ImageIcon className="w-5 h-5 text-white/30" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-white truncate">{item.name}</p>
                      <p className="text-xs text-white/50 truncate">{item.subtitle}</p>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Custom Snooze Dialog */}
      <Dialog open={customSnoozeOpen} onOpenChange={setCustomSnoozeOpen}>
        <DialogContent className="bg-[#0A0A0A] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle>Custom Snooze</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Snooze for (minutes)</Label>
              <Input
                type="number"
                min="1"
                value={customSnoozeMinutes}
                onChange={(e) => setCustomSnoozeMinutes(parseInt(e.target.value) || 60)}
                className="bg-black/50 border-white/10"
              />
            </div>
            <Button
              onClick={() => {
                if (snoozeTargetId) {
                  handleSnooze(snoozeTargetId, customSnoozeMinutes);
                }
                setCustomSnoozeOpen(false);
                setSnoozeTargetId(null);
              }}
              className="w-full bg-purple-600 hover:bg-purple-500"
            >
              Snooze
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Filters & Sort */}
      <Card className="bg-[#0A0A0A] border-white/10">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/50" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search notifications..."
                className="pl-10 bg-black/50 border-white/10"
              />
            </div>
            <Select value={sortBy} onValueChange={setSortBy}>
              <SelectTrigger className="w-40 bg-black/50 border-white/10">
                <SelectValue placeholder="Sort by" />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                {sortOptions.map((opt) => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={sortOrder} onValueChange={setSortOrder}>
              <SelectTrigger className="w-32 bg-black/50 border-white/10">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#0A0A0A] border-white/10">
                <SelectItem value="asc">{sortBy === "upcoming" ? "Soonest" : "A → Z"}</SelectItem>
                <SelectItem value="desc">{sortBy === "upcoming" ? "Latest" : "Z → A"}</SelectItem>
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={showDisabled}
                onCheckedChange={setShowDisabled}
              />
              <span className="text-sm text-white/70">Show disabled</span>
            </label>
          </div>
        </CardContent>
      </Card>

      {/* Notifications List */}
      <div className="space-y-4">
        {loading ? (
          <div className="flex justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-white/50" />
          </div>
        ) : filteredNotifications.length === 0 ? (
          <Card className="bg-[#0A0A0A] border-white/10">
            <CardContent className="p-12 text-center">
              <Bell className="w-12 h-12 mx-auto mb-4 text-white/30" />
              <p className="text-white/50">No notifications yet</p>
              <p className="text-sm text-white/30 mt-1">Create your first notification to get started</p>
            </CardContent>
          </Card>
        ) : (
          filteredNotifications.map((notification) => {
            const ScheduleIcon = getScheduleIcon(notification.scheduleType);
            const relativeTime = getRelativeTime(notification.nextTrigger);
            
            return (
              <Card
                key={notification.id}
                className={`bg-[#0A0A0A] border-white/10 transition-opacity ${
                  !notification.isEnabled ? "opacity-50" : ""
                }`}
                data-testid={`notification-${notification.id}`}
              >
                <CardContent className="p-4">
                  <div className="flex items-start gap-4">
                    {/* Linked Item Image */}
                    <div className="flex-shrink-0">
                      {notification.linkedItemImage ? (
                        <img
                          src={notification.linkedItemImage}
                          alt={notification.linkedItemName}
                          className="w-16 h-16 rounded-lg object-cover bg-white/10"
                        />
                      ) : (
                        <div className="w-16 h-16 rounded-lg bg-purple-500/20 flex items-center justify-center">
                          <Bell className="w-8 h-8 text-purple-400" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-4">
                        <div>
                          <h3 className="font-medium text-white text-lg">{notification.name}</h3>
                          {notification.linkedItemName && (
                            <p className="text-sm text-white/50 flex items-center gap-1 mt-0.5">
                              {notification.linkedItemType === "game" && <Gamepad2 className="w-3 h-3" />}
                              {notification.linkedItemType === "achievement" && <Trophy className="w-3 h-3" />}
                              {notification.linkedItemType === "challenge" && <Target className="w-3 h-3" />}
                              {notification.linkedItemName}
                            </p>
                          )}
                        </div>
                        
                        {/* Actions */}
                        <div className="flex items-center gap-2">
                          {/* Toggle */}
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleToggle(notification.id)}
                            className={notification.isEnabled ? "text-green-400 hover:text-green-300" : "text-white/30 hover:text-white/50"}
                          >
                            {notification.isEnabled ? <ToggleRight className="w-5 h-5" /> : <ToggleLeft className="w-5 h-5" />}
                          </Button>

                          {/* Snooze Menu */}
                          {notification.isEnabled && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className={notification.isSnoozed ? "text-amber-400 hover:text-amber-300" : "text-white/50 hover:text-white"}
                                >
                                  {notification.isSnoozed ? <AlarmClockOff className="w-5 h-5" /> : <AlarmClock className="w-5 h-5" />}
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                                {notification.isSnoozed ? (
                                  <DropdownMenuItem onClick={() => handleUnsnooze(notification.id)}>
                                    <BellRing className="w-4 h-4 mr-2" />
                                    Remove Snooze
                                  </DropdownMenuItem>
                                ) : (
                                  <>
                                    {snoozeOptions.map((opt) => (
                                      <DropdownMenuItem
                                        key={opt.value}
                                        onClick={() => handleSnooze(notification.id, opt.value)}
                                      >
                                        <Clock className="w-4 h-4 mr-2" />
                                        {opt.label}
                                      </DropdownMenuItem>
                                    ))}
                                    <DropdownMenuSeparator className="bg-white/10" />
                                    <DropdownMenuItem
                                      onClick={() => {
                                        setSnoozeTargetId(notification.id);
                                        setCustomSnoozeOpen(true);
                                      }}
                                    >
                                      <Timer className="w-4 h-4 mr-2" />
                                      Custom...
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}

                          {/* More Actions */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="text-white/50 hover:text-white">
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#0A0A0A] border-white/10">
                              <DropdownMenuItem onClick={() => openEditDialog(notification)}>
                                <Edit2 className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleDuplicate(notification.id)}>
                                <Copy className="w-4 h-4 mr-2" />
                                Duplicate
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-white/10" />
                              <AlertDialog>
                                <AlertDialogTrigger asChild>
                                  <DropdownMenuItem
                                    onSelect={(e) => e.preventDefault()}
                                    className="text-red-400 focus:text-red-400"
                                  >
                                    <Trash2 className="w-4 h-4 mr-2" />
                                    Delete
                                  </DropdownMenuItem>
                                </AlertDialogTrigger>
                                <AlertDialogContent className="bg-[#0A0A0A] border-white/10">
                                  <AlertDialogHeader>
                                    <AlertDialogTitle className="text-white">Delete Notification?</AlertDialogTitle>
                                    <AlertDialogDescription>
                                      This will permanently delete "{notification.name}". This action cannot be undone.
                                    </AlertDialogDescription>
                                  </AlertDialogHeader>
                                  <AlertDialogFooter>
                                    <AlertDialogCancel className="bg-white/5 border-white/10 text-white hover:bg-white/10">
                                      Cancel
                                    </AlertDialogCancel>
                                    <AlertDialogAction
                                      onClick={() => handleDelete(notification.id)}
                                      className="bg-red-600 hover:bg-red-700"
                                    >
                                      Delete
                                    </AlertDialogAction>
                                  </AlertDialogFooter>
                                </AlertDialogContent>
                              </AlertDialog>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>

                      {/* Schedule Info */}
                      <div className="flex flex-wrap items-center gap-4 mt-3 text-sm">
                        <div className="flex items-center gap-2 text-white/60">
                          <ScheduleIcon className="w-4 h-4" />
                          {getScheduleDescription(notification)}
                        </div>
                        
                        {notification.nextTrigger && (
                          <div className="flex items-center gap-2">
                            <div className={`flex items-center gap-2 px-2 py-1 rounded-full text-xs ${
                              notification.isSnoozed
                                ? "bg-amber-500/20 text-amber-400"
                                : relativeTime === "Overdue"
                                  ? "bg-red-500/20 text-red-400"
                                  : "bg-purple-500/20 text-purple-400"
                            }`}>
                              <Clock className="w-3 h-3" />
                              {notification.isSnoozed ? "Snoozed until " : "Next: "}
                              {formatDate(notification.nextTrigger)}
                              {relativeTime && ` (${relativeTime})`}
                            </div>
                            {/* Calendar Integration - Coming Soon */}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2 text-white/30 cursor-not-allowed"
                              onClick={(e) => {
                                e.stopPropagation();
                                toast.info("Calendar sync coming soon!", {
                                  description: "We're working on a more robust notification system."
                                });
                              }}
                              title="Coming Soon"
                            >
                              <Calendar className="w-3 h-3 mr-1" />
                              <span className="text-xs">Coming Soon</span>
                            </Button>
                          </div>
                        )}
                        
                        {notification.triggerCount > 0 && (
                          <span className="text-white/40 text-xs">
                            Triggered {notification.triggerCount} time{notification.triggerCount !== 1 ? "s" : ""}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })
        )}
      </div>

      {/* AdSense */}
      <AdBanner />
    </div>
  );
}
